import { unstable_cache } from 'next/cache';

import {
	getCachedEmptyShortsAfterQuota,
	isYouTubeShortsDisabledByEnv,
	isYouTubeShortsQuotaBlocked,
	markYouTubeShortsQuotaExceeded,
} from './youtube-quota-cache';

const YOUTUBE_API_KEY = process.env.YOUTUBE_API_KEY;
const YOUTUBE_CHANNEL_ID = process.env.YOUTUBE_CHANNEL_ID;
const YOUTUBE_API_BASE_URL = 'https://www.googleapis.com/youtube/v3';
const SHORTS_CACHE_DURATION = 24 * 60 * 60;
const SHORTS_QUOTA_COOLDOWN_MS = SHORTS_CACHE_DURATION * 1000;

let shortsQuotaCooldownUntil = 0;
let shortsFetchInFlight = null;
let shortsFetchInFlightKey = null;
let shortsQuota429Logged = false;

export function isYouTubeShortsInProcessCooldown() {
	return Date.now() < shortsQuotaCooldownUntil;
}

function isYouTubeConfigured() {
	return Boolean(YOUTUBE_API_KEY && YOUTUBE_CHANNEL_ID);
}

function redactYouTubeApiKey(urlString) {
	return String(urlString).replace(/([?&]key=)[^&]*/, '$1***');
}

function isYouTubeQuotaError(error) {
	const message = String(error?.message || '').toLowerCase();
	return (
		error?.status === 429 ||
		(message.includes('quota') && message.includes('exceeded'))
	);
}

class YouTubeAPI {
	constructor() {
		this.baseURL = YOUTUBE_API_BASE_URL;
		this.apiKey = YOUTUBE_API_KEY;
		this.channelId = YOUTUBE_CHANNEL_ID;
	}

	buildURL(endpoint, params = {}) {
		const url = new URL(`${this.baseURL}/${endpoint}`);
		url.searchParams.append('key', this.apiKey);
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				url.searchParams.append(key, value);
			}
		});
		return url.toString();
	}

	async request(endpoint, params = {}, cacheOptions = {}) {
		const { revalidate = SHORTS_CACHE_DURATION } = cacheOptions;
		const url = this.buildURL(endpoint, params);
		const response = await fetch(url, {
			method: 'GET',
			headers: { Accept: 'application/json' },
			next: { revalidate },
		});

		if (!response.ok) {
			const error = await response.json().catch(() => ({}));
			const apiError = new Error(
				`YouTube API Error (${response.status}): ${error.error?.message || response.statusText}`
			);
			apiError.status = response.status;
			throw apiError;
		}

		return response.json();
	}

	formatSearchResultItems(items) {
		if (!items?.length) return [];
		return items
			.map((item) => {
				const id = item.id?.videoId;
				const snippet = item.snippet;
				if (!id || !snippet) return null;

				return {
					id,
					videoId: id,
					title: snippet.title,
					description: snippet.description ?? '',
					publishedAt: snippet.publishedAt,
					url: `https://www.youtube.com/watch?v=${id}`,
					embedUrl: `https://www.youtube.com/embed/${id}`,
					thumbnail:
						snippet.thumbnails?.maxres?.url ||
						snippet.thumbnails?.standard?.url ||
						snippet.thumbnails?.high?.url ||
						snippet.thumbnails?.medium?.url ||
						snippet.thumbnails?.default?.url,
					channel: {
						id: snippet.channelId,
						title: snippet.channelTitle,
					},
				};
			})
			.filter(Boolean);
	}

	async fetchChannelShorts({ maxResults = 10 } = {}) {
		if (!isYouTubeConfigured()) {
			return [];
		}
		if (isYouTubeShortsDisabledByEnv()) {
			return [];
		}
		if (isYouTubeShortsInProcessCooldown()) {
			return getCachedEmptyShortsAfterQuota().catch(() => []);
		}
		if (await isYouTubeShortsQuotaBlocked()) {
			return getCachedEmptyShortsAfterQuota();
		}

		const validMaxResults = Math.min(Math.max(1, maxResults), 50);
		const flightKey = String(validMaxResults);

		if (shortsFetchInFlight && shortsFetchInFlightKey === flightKey) {
			return shortsFetchInFlight;
		}

		shortsFetchInFlightKey = flightKey;
		shortsFetchInFlight = this._fetchChannelShortsUncached(validMaxResults).finally(
			() => {
				if (shortsFetchInFlightKey === flightKey) {
					shortsFetchInFlight = null;
					shortsFetchInFlightKey = null;
				}
			}
		);

		return shortsFetchInFlight;
	}

	async _fetchChannelShortsUncached(validMaxResults) {
		const searchParams = {
			part: 'snippet',
			channelId: this.channelId,
			maxResults: validMaxResults,
			order: 'date',
			type: 'video',
			videoDuration: 'short',
			videoDefinition: 'any',
		};

		try {
			if (process.env.NODE_ENV === 'development') {
				console.log(
					'[YouTube Shorts] URL search:',
					redactYouTubeApiKey(this.buildURL('search', searchParams))
				);
			}

			const searchResponse = await this.request('search', searchParams, {
				revalidate: SHORTS_CACHE_DURATION,
			});

			if (!searchResponse.items?.length) {
				return [];
			}

			return this.formatSearchResultItems(searchResponse.items).slice(
				0,
				validMaxResults
			);
		} catch (error) {
			if (isYouTubeQuotaError(error)) {
				shortsQuotaCooldownUntil = Date.now() + SHORTS_QUOTA_COOLDOWN_MS;
				await markYouTubeShortsQuotaExceeded();
				if (!shortsQuota429Logged) {
					shortsQuota429Logged = true;
					console.warn(
						'[YouTube Shorts] Quota excedida; pausando novas tentativas por 24h.'
					);
				}
				return getCachedEmptyShortsAfterQuota();
			}
			throw error;
		}
	}
}

const youtubeAPIInternal = new YouTubeAPI();

const cachedFetchChannelShorts = unstable_cache(
	async (maxResults) => {
		return youtubeAPIInternal.fetchChannelShorts({ maxResults });
	},
	['youtube-channel-shorts-v5-search-only'],
	{
		revalidate: SHORTS_CACHE_DURATION,
		tags: ['youtube', 'channel-shorts', 'youtube-quota-shorts'],
	}
);

export const youtubeAPI = {
	fetchChannelShorts(options = {}) {
		const maxResults = options?.maxResults ?? 10;
		return cachedFetchChannelShorts(maxResults);
	},
};

export function extractYouTubeVideoId(url) {
	if (!url || typeof url !== 'string') return null;
	const patterns = [
		/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
		/youtube\.com\/shorts\/([^&\n?#]+)/,
	];
	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match) return match[1];
	}
	return null;
}
